import { useEffect, useState } from "react";

import useGraphQLClient from "./useGraphQLClient";

import { READ_SOP_LIBRARY_FILTER_DATA } from "../graph/projects/queries";
import { READ_SOP_TASK_PROGRESS } from "../graph/tasks/queries";

const emptyDashboard = {
  summary: {
    assignedCount: 0,
    readCount: 0,
    understoodCount: 0,
    progressPercent: 0,
  },
  nextActionRows: [],
  sopRows: [],
};

const useStaffKnowledgeDashboard = ({
  tenantId,
  userId,
}) => {
  const client = useGraphQLClient();

  const [data, setData] = useState(emptyDashboard);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!tenantId || !userId) {
      setData(emptyDashboard);
      setLoading(false);
      setError(null);
      return;
    }

    let isMounted = true;

    const loadDashboard = async () => {
      setLoading(true);
      setError(null);

      try {
        const {
          readRoleAllocationBoard,
        } = await client.request(
          READ_SOP_LIBRARY_FILTER_DATA,
          {
            input: {
              tenantId,
            },
          }
        );

        if (!isMounted) {
          return;
        }

        const assignedSops = getAssignedApprovedSops({
          allocationBoard: readRoleAllocationBoard,
          userId,
        });

        const sopRows = await Promise.all(
          assignedSops.map(async (sop) => {
            const {
              readSOPTaskProgress,
            } = await client.request(
              READ_SOP_TASK_PROGRESS,
              {
                tenantId,
                sopId: sop.id,
              }
            );
            

            return buildSopRow({
              sop,
              taskProgress: readSOPTaskProgress || [],
            });
          })
        );

        if (!isMounted) {
          return;
        }

        const understoodCount = sopRows.filter(
          (sop) =>
            sop.understoodStatus === "Understood"
        ).length;

        const readCount = sopRows.filter(
          (sop) =>
            sop.readStatus === "Read" ||
            sop.readStatus === "Complete"
        ).length;

        const assignedCount = sopRows.length;

        const progressPercent =
          assignedCount > 0
            ? Math.round(
                (understoodCount / assignedCount) * 100
              )
            : 0;

        const nextActionRows = sopRows
          .filter(
            (sop) =>
              sop.understoodStatus !== "Understood"
          )
          .map((sop) => ({
            id: sop.id,
            sopId: sop.id,
            sopTitle: sop.sopTitle,
            departmentName: sop.departmentName,
            action:
              sop.readStatus === "Not started"
                ? "Read SOP"
                : "Mark Understood",
          }));

        setData({
          summary: {
            assignedCount,
            readCount,
            understoodCount,
            progressPercent,
          },
          nextActionRows,
          sopRows,
        });
      } catch (err) {
        if (!isMounted) {
          return;
        }

        console.error(
          "Failed loading staff knowledge dashboard",
          err
        );

        setError(err);
        setData(emptyDashboard);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      isMounted = false;
    };
  }, [tenantId, userId]);

  return {
    data,
    loading,
    error,
  };
};

const getAssignedApprovedSops = ({
  allocationBoard,
  userId,
}) => {
  const roles = allocationBoard?.roles || [];
  const departments =
    allocationBoard?.departments || [];

  const sopMap = new Map();

  roles.forEach((roleItem) => {
    const isAssignedToUser =
      roleItem.peopleCards?.some(
        (person) =>
          person.tenantUser?.userId === userId
      );

    if (!isAssignedToUser) {
      return;
    }

    const role = roleItem.role;

    const department = departments.find(
      (item) => item.id === role?.departmentId
    );

    roleItem.sopCards?.forEach((card) => {
      const sop = card.sop;

      if (!sop?.id) {
        return;
      }

      // Staff learning must only use approved content.
      if (sop.approvalStatus !== "APPROVED") {
        return;
      }

      const existing = sopMap.get(sop.id);

      const departmentNames = [
        ...(existing?.departmentNames || []),
        department?.name,
      ].filter(Boolean);

      sopMap.set(sop.id, {
        ...sop,
        departmentNames: [
          ...new Set(departmentNames),
        ],
      });
    });
  });

  return Array.from(sopMap.values());
};

const buildSopRow = ({
  sop,
  taskProgress,
}) => {
  const currentTaskProgress = taskProgress || [];

  const statuses = currentTaskProgress.map(
    (progress) => progress.status || "NEW"
  );

  const hasTasks = statuses.length > 0;

  const allUnderstood =
    hasTasks &&
    statuses.every(
      (status) => status === "UNDERSTOOD"
    );

  const hasCurrentRead = statuses.some(
    (status) =>
      status === "READ" ||
      status === "UNDERSTOOD"
  );

  let readStatus = "Not started";
  let understoodStatus = "Not started";

  if (allUnderstood) {
    readStatus = "Complete";
    understoodStatus = "Understood";
  } else if (hasCurrentRead) {
    readStatus = "Read";
    understoodStatus = "Pending";
  }

  return {
    id: sop.id,
    sopId: sop.id,
    sopTitle: sop.title,
    departmentName:
      sop.departmentNames?.join(", ") ||
      "Not assigned",
    readStatus,
    understoodStatus,
  };
};

export default useStaffKnowledgeDashboard;